import { useState } from 'react';
import { Loader2, Upload, X, Newspaper, Check } from 'lucide-react';
import { sendEditorialMessage, uploadEditorialFile } from '@generated/server/editorial';

const TYPES = [
  { id: 'News Tip', hint: 'Something happening that we should know about' },
  { id: 'Suggestion', hint: 'How we could make the papers better' },
  { id: 'Story Idea', hint: 'A person, place or topic worth a story' },
] as const;
const PAPERS = ['Red Hook Star-Revue', 'The Village Star-Revue', 'Either / Both'] as const;
type Kind = (typeof TYPES)[number]['id'];
type Paper = (typeof PAPERS)[number];

const input = 'h-11 w-full border border-input bg-background px-3 text-sm';
const label = 'text-xs font-semibold uppercase tracking-wider';

export function Editorial() {
  const [type, setType] = useState<Kind>('News Tip');
  const [paper, setPaper] = useState<Paper>('Either / Both');
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<{ note: string | null; emailed: boolean; notified: boolean; why: string | null } | null>(null);

  const ready = message.trim().length >= 5 && (anonymous || email.trim().length > 3 || phone.trim().length > 6);

  const send = async () => {
    setSending(true); setErr(null);
    try {
      const res = await sendEditorialMessage({ data: { type, paper, message, anonymous, senderName: name, email, phone } });
      let note: string | null = null;
      for (const f of files) {
        try { const fd = new FormData(); fd.append('file', f); fd.append('itemId', res?.id ?? ''); await uploadEditorialFile({ data: fd }); }
        catch (e) { console.error(e); note = `${f.name} could not be attached. Please email it to gbrook8344@gmail.com.`; }
      }
      setSent({ note, emailed: res?.emailed ?? false, notified: res?.notified ?? false, why: res?.emailWhy ?? null });
      window.scrollTo({ top: 0 });
    } catch (e) {
      console.error(e); setErr(e instanceof Error ? e.message : 'Your message could not be sent.');
    } finally { setSending(false); }
  };
  const reset = () => { setSent(null); setMessage(''); setFiles([]); };

  if (sent) return (
    <section className="mx-auto max-w-2xl border-y-4 border-double border-foreground py-10 text-center">
      <Check className="mx-auto h-10 w-10 text-primary" />
      <h2 className="mt-3 font-[family-name:var(--font-heading)] text-3xl font-semibold">Thank you. Your message is in.</h2>
      <p className="mt-3 text-sm text-muted-foreground">It went straight to our news department. {anonymous ? 'You sent it anonymously, so we won\'t be able to reply.' : 'If we follow up, we\'ll contact you directly.'}</p>
      {sent.note && <p className="mt-3 text-sm text-destructive">{sent.note}</p>}
      <p className="mt-4 text-xs text-muted-foreground">{sent.emailed ? "Email notice to the news desk: sent." : sent.notified ? "The news desk has been notified." : `Email notice to the news desk: not sent (${sent.why ?? "unknown"}). Your message is still saved.`}</p>
      <button onClick={reset} className="mt-6 min-h-11 bg-foreground px-6 text-sm font-semibold text-background">Send another message</button>
    </section>
  );

  return (
    <section className="grid gap-8 lg:grid-cols-[300px_1fr]">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Editorial</div>
        <h2 className="mt-2 font-[family-name:var(--font-heading)] text-3xl font-semibold leading-tight">Send a message to our news department</h2>
        <p className="mt-3 text-sm text-muted-foreground">A tip, a suggestion, or an idea. Our reporters and editors read every message.</p>
        <Newspaper className="mt-6 hidden h-16 w-16 text-muted-foreground/40 lg:block" />
      </div>
      <div className="min-w-0 space-y-6">
        <fieldset><legend className={label}>What kind of message?</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">{TYPES.map((t) => (
            <button key={t.id} type="button" aria-pressed={type === t.id} onClick={() => setType(t.id)} className={`min-h-11 border p-3 text-left ${type === t.id ? 'border-foreground bg-foreground text-background' : 'border-border bg-card hover:border-foreground/50'}`}>
              <div className="text-sm font-semibold">{t.id}</div><div className="text-xs opacity-75">{t.hint}</div>
            </button>))}</div>
        </fieldset>
        <fieldset><legend className={label}>Which paper?</legend>
          <div className="mt-2 flex flex-wrap gap-2">{PAPERS.map((p) => (
            <button key={p} type="button" aria-pressed={paper === p} onClick={() => setPaper(p)} className={`min-h-11 border px-4 text-sm ${paper === p ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{p}</button>))}</div>
        </fieldset>
        <div><label htmlFor="ed-msg" className={label}>Your message</label>
          <textarea id="ed-msg" value={message} onChange={(e) => setMessage(e.target.value)} className="mt-2 min-h-40 w-full border border-input bg-background p-3 text-sm" placeholder="What, where, when, and who we could talk to…" /></div>
        <div>
          <span className={label}>Photos or files (optional)</span>
          <label className="mt-2 flex min-h-11 w-fit cursor-pointer items-center gap-2 border border-foreground px-4 text-sm font-semibold"><Upload className="h-4 w-4" />Add photos or files
            <input type="file" multiple className="sr-only" onChange={(e) => setFiles((f) => [...f, ...Array.from(e.target.files ?? [])].slice(0, 8))} /></label>
          {files.length > 0 && <ul className="mt-2 space-y-1 text-sm">{files.map((f) => <li key={`${f.name}-${f.size}-${f.lastModified}`} className="flex items-center gap-2">{f.name}<button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((l) => l.filter((x) => x !== f))} className="flex h-9 w-9 items-center justify-center"><X className="h-4 w-4" /></button></li>)}</ul>}
        </div>
        <div className="border-t border-border pt-5">
          <label className="flex min-h-11 items-center gap-2 text-sm font-medium"><input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="h-5 w-5" />Keep me anonymous. Don't send my name or contact details.</label>
          {!anonymous && <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div><label htmlFor="ed-name" className="text-sm">Name</label><input id="ed-name" value={name} onChange={(e) => setName(e.target.value)} className={input} /></div>
            <div><label htmlFor="ed-email" className="text-sm">Email</label><input id="ed-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={input} /></div>
            <div><label htmlFor="ed-phone" className="text-sm">Phone</label><input id="ed-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={input} /></div>
            <p className="text-xs text-muted-foreground sm:col-span-3">An email or phone number lets us follow up. We never publish your details without asking.</p>
          </div>}
        </div>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <button onClick={() => void send()} disabled={!ready || sending} className="inline-flex min-h-11 items-center gap-2 bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-50">{sending && <Loader2 className="h-4 w-4 animate-spin" />}Send to the news department</button>
      </div>
    </section>
  );
}
